/**
 * Alpha Vantage API Provider
 * 需要API密钥，支持股票、指数、外汇和加密货币
 */

import {
  ApiProvider,
  Asset,
  AssetPrice,
  AssetType,
  TimeRange,
  HistoricalData,
  AssetSearchResult,
  AssetValidationResult,
  RequestConfig
} from '@/types/api';

import { alphavantageService } from '../alphavantage';
import { createApiError, categorizeHttpError, extractRetryAfter } from '../errors';
import { getApiKey } from '../apiKeyManager';

export class AlphaVantageProvider implements ApiProvider {
  name = 'alphavantage';
  priority = 3; // 第三优先级
  isHealthy = true;
  lastError?: Date;
  supportedAssetTypes: AssetType[] = ['crypto', 'stock', 'forex']; // Note: Alpha Vantage doesn't support indices via GLOBAL_QUOTE
  
  private readonly timeout = 10000;
  
  async getAssetPrice(asset: Asset, config?: RequestConfig): Promise<AssetPrice> {
    try {
      const apiKey = getApiKey('alphavantage');
      if (!apiKey) {
        throw createApiError('AUTH_ERROR', 'Alpha Vantage API key is required', { provider: this.name });
      }
      
      alphavantageService.setApiKey(apiKey);
      
      let result: AssetPrice;
      switch (asset.type) {
        case 'stock':
          result = await alphavantageService.getStockPrice(asset.symbol);
          break;
        case 'index':
          // Alpha Vantage doesn't support indices via GLOBAL_QUOTE
          // Skip index validation for Alpha Vantage
          throw createApiError(
            'VALIDATION_ERROR',
            `Alpha Vantage does not support index type assets`,
            { provider: this.name }
          );
        case 'forex':
          const [from, to] = this.parseForexSymbol(asset.symbol);
          result = await alphavantageService.getForexRate(from, to);
          break;
        case 'crypto':
          const results = await alphavantageService.getCryptoPrice([asset.id]);
          if (results.length === 0) throw new Error(`Crypto asset not found: ${asset.symbol}`);
          result = results[0];
          break;
        default:
          throw createApiError('VALIDATION_ERROR', `Unsupported asset type: ${asset.type}`, { provider: this.name });
      }
      
      return {
        ...result,
        id: asset.id,
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }
  
  async getAssetPrices(assets: Asset[], config?: RequestConfig): Promise<AssetPrice[]> {
    try {
      const apiKey = getApiKey('alphavantage');
      if (!apiKey) {
        throw createApiError('AUTH_ERROR', 'Alpha Vantage API key is required', { provider: this.name });
      }
      
      alphavantageService.setApiKey(apiKey);
      
      const results: AssetPrice[] = [];
      for (const asset of assets) {
        try {
          const price = await this.getAssetPrice(asset, config);
          results.push(price);
        } catch (error) {
          console.warn(`Failed to get price for ${asset.symbol}:`, error);
        }
      }
      return results;
    } catch (error) {
      throw this.handleError(error);
    }
  }
  
  async getHistoricalData(asset: Asset, timeRange: TimeRange, config?: RequestConfig): Promise<HistoricalData> {
    try {
      const apiKey = getApiKey('alphavantage');
      if (!apiKey) {
        throw createApiError('AUTH_ERROR', 'Alpha Vantage API key is required', { provider: this.name });
      }
      
      alphavantageService.setApiKey(apiKey);
      const days = this.timeRangeToDays(timeRange);
      const rawData = await alphavantageService.getHistoricalData(
        asset.id,
        asset.symbol,
        asset.type === 'crypto' ? 'crypto' : asset.type === 'forex' ? 'forex' : 'stock',
        days
      );
      
      return {
        asset,
        timeRange,
        data: {
          prices: rawData.prices.map(([t, v]) => ({ timestamp: t, value: v })),
          volumes: rawData.total_volumes.map(([t, v]) => ({ timestamp: t, value: v })),
          marketCaps: rawData.market_caps?.map(([t, v]) => ({ timestamp: t, value: v }))
        },
        metadata: { fetchedAt: new Date(), source: this.name, interval: this.getInterval(timeRange) }
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }
  
  async searchAssets(query: string, type?: AssetType, config?: RequestConfig): Promise<AssetSearchResult[]> {
    return this.getKnownAssets(type).filter(a => 
      a.symbol.toLowerCase().includes(query.toLowerCase()) || 
      a.name.toLowerCase().includes(query.toLowerCase())
    );
  }
  
  async validateAsset(asset: Partial<Asset>, config?: RequestConfig): Promise<AssetValidationResult> {
    if (!asset.symbol || !asset.type) {
      return { valid: false, error: 'Asset symbol and type are required' };
    }
    
    // For symbols with exchange suffix (e.g., 0700.HK, 600519.SH), preserve the format
    const symbol = asset.symbol.toUpperCase();
    const testAsset: Asset = {
      id: asset.id || symbol.toLowerCase().replace(/\./g, '_'),
      symbol: symbol, // Preserve original symbol format including exchange suffix
      name: asset.name || symbol,
      type: asset.type,
      priceStatus: 'loading',
      displayOrder: 0,
      isActive: true,
      ...(asset.exchange && { exchange: asset.exchange })
    };
    
    try {
      const priceData = await this.getAssetPrice(testAsset, { ...config, timeout: 5000 });
      return { valid: true, asset: { ...testAsset, price: priceData.current_price, priceStatus: 'success' as const } };
    } catch (error) {
      return { valid: false, error: `Validation failed: ${error instanceof Error ? error.message : 'Unknown error'}` };
    }
  }
  
  async testConnection(config?: RequestConfig): Promise<boolean> {
    try {
      const apiKey = getApiKey('alphavantage');
      if (!apiKey || apiKey.trim() === '') return false;
      
      const testAsset: Asset = { id: 'appl', symbol: 'APPL', name: 'Apple', type: 'stock', priceStatus: 'loading', displayOrder: 0, isActive: true };
      await this.getAssetPrice(testAsset, { ...config, timeout: 5000 });
      return true;
    } catch {
      return false;
    }
  }
  
  private parseForexSymbol(symbol: string): [string, string] {
    if (symbol.includes('/')) {
      const [from, to] = symbol.split('/');
      return [from.trim(), to.trim()];
    }
    if (symbol.includes('_')) {
      const [from, to] = symbol.split('_');
      return [from.trim(), to.trim()];
    }
    if (symbol.length === 6) {
      return [symbol.substring(0, 3), symbol.substring(3)];
    }
    throw new Error(`Invalid forex symbol format: ${symbol}`);
  }
  
  private timeRangeToDays(timeRange: TimeRange): number {
    const map: Record<TimeRange, number> = { '1D': 1, '7D': 7, '30D': 30, '90D': 90, '1Y': 365 };
    return map[timeRange] || 7;
  }
  
  private getInterval(timeRange: TimeRange): string {
    const map: Record<TimeRange, string> = { '1D': '1h', '7D': '4h', '30D': '1d', '90D': '1d', '1Y': '1d' };
    return map[timeRange] || '1d';
  }
  
  private getKnownAssets(type?: AssetType): AssetSearchResult[] {
    const all: AssetSearchResult[] = [
      { id: 'appl', symbol: 'APPL', name: 'Apple Inc.', type: 'stock' },
      { id: 'msft', symbol: 'MSFT', name: 'Microsoft', type: 'stock' },
      { id: 'spx', symbol: 'SPX', name: 'S&P 500', type: 'index' },
      { id: 'usd_eur', symbol: 'USD/EUR', name: 'USD/EUR', type: 'forex' },
      { id: 'bitcoin', symbol: 'BTC', name: 'Bitcoin', type: 'crypto' }
    ];
    return type ? all.filter(a => a.type === type) : all;
  }
  
  private handleError(error: unknown): never {
    if (error instanceof Error && 'code' in error) throw error;
    if (error instanceof Error) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw createApiError('NETWORK_ERROR', 'Network connection failed', { provider: this.name, originalError: error });
      }
      if (error.name === 'AbortError' || error.message.includes('timeout')) {
        throw createApiError('TIMEOUT_ERROR', 'Request timed out', { provider: this.name, originalError: error });
      }
      if ('response' in error && error.response) {
        const response = error.response as any;
        const retryAfter = response.headers ? extractRetryAfter(response.headers as Record<string, string>) : undefined;
        throw createApiError(
          categorizeHttpError(response.status, response.data?.message),
          response.data?.message || error.message,
          { provider: this.name, originalError: error, ...(retryAfter ? { retryAfter } : {}) }
        );
      }
      throw createApiError('UNKNOWN_ERROR', error.message, { provider: this.name, originalError: error });
    }
    throw createApiError('UNKNOWN_ERROR', 'An unknown error occurred', { provider: this.name, originalError: error });
  }
}
